import {
  AnswerType,
  EntityRef,
  FormBlock,
  FormQuestionOption,
} from "vmd-types";

// Re-derive each item's `order` from its array index after add/remove/reorder.
// Items already carrying the right order keep their identity, so editing one
// question in a large block does not replace every sibling object and force the
// whole list to re-render.
export const withOrder = <T extends { order: number }>(items: T[]): T[] => {
  let changed = false;
  const next = items.map((item, index) => {
    if (item.order === index) return item;
    changed = true;
    return { ...item, order: index };
  });
  return changed ? next : items;
};

export const BLOCK_TRANSITION_MS = 180;

export type ConditionCandidateLike = {
  id: string;
  label: string;
  answerType: AnswerType;
  options?: FormQuestionOption[];
  entityRef?: EntityRef;
};

/** The label a "conditional on" dropdown shows for a question. */
const candidateLabel = (question: {
  code?: string;
  label?: string;
  fieldName?: string;
}): string => {
  const text = question.label?.trim() || question.fieldName?.trim() || "";
  if (!text) return "Untitled question";
  const code = question.code?.trim();
  return code ? `${code} - ${text}` : text;
};

export const buildConditionCandidates = (
  blocks: FormBlock[]
): ConditionCandidateLike[] =>
  blocks.flatMap((block) =>
    (block.questions ?? []).map((question) => ({
      id: question.id,
      label: candidateLabel(question),
      answerType: question.answerType,
      options: question.options,
      entityRef: question.entityRef,
    }))
  );

/**
 * Content key for the "conditional on" candidate list, covering only what that
 * dropdown and its "equals" companion render. Memoizing the list on this key
 * rather than on the blocks array keeps one identity across edits the dropdowns
 * do not show (description, mandatory, enabled), which is what lets the question
 * editors stay memoized while a sibling is edited.
 */
export const conditionCandidateKey = (blocks: FormBlock[]): string =>
  blocks
    .flatMap((block) =>
      (block.questions ?? []).map(
        (question) =>
          `${question.id}\0${candidateLabel(question)}\0${question.answerType}\0${question.entityRef ?? ""}\0${(
            question.options ?? []
          )
            .map((option) => `${option.id}:${option.label}`)
            .join("\x01")}`
      )
    )
    .join("\x02");

const normalizeKey = (value?: string): string =>
  value
    ?.replace(/<[^>]*>/g, "")
    .replace(/\s+/g, " ")
    .trim()
    .toLowerCase() ?? "";

const indexBy = <T>(items: T[], key: (item: T) => string): Map<string, T> => {
  const map = new Map<string, T>();
  for (const item of items) {
    const value = key(item);
    if (value && !map.has(value)) map.set(value, item);
  }
  return map;
};

// One key can name several questions — a workbook may repeat a label inside a
// block — so each key holds every id under it, in sheet order.
const indexIdsBy = <T extends { id: string }>(
  items: T[],
  key: (item: T) => string
): Map<string, string[]> => {
  const map = new Map<string, string[]>();
  for (const item of items) {
    const value = key(item);
    if (!value) continue;
    map.set(value, [...(map.get(value) ?? []), item.id]);
  }
  return map;
};

// Repeated keys match in sheet order — the nth "OE Name" takes the nth stored
// one — so a duplicate cannot strand every row after it.
const makeIdTaker = () => {
  const claimed = new Set<string>();
  return (index: Map<string, string[]>, key: string): string | undefined => {
    const queue = index.get(key);
    if (!queue) return undefined;
    while (queue.length > 0) {
      const id = queue.shift() as string;
      if (!claimed.has(id)) {
        claimed.add(id);
        return id;
      }
    }
    return undefined;
  };
};

// An answer stores the option id and the importer mints a fresh one per upload,
// so the stored value only stays reachable if the ids travel with the labels.
const reuseOptionIds = (
  stored: FormQuestionOption[] | undefined,
  imported: FormQuestionOption[] | undefined,
  into: Map<string, string>
): FormQuestionOption[] | undefined => {
  if (!imported?.length || !stored?.length) return imported;
  const byLabel = indexIdsBy(stored, (option) => normalizeKey(option.label));
  const take = makeIdTaker();
  return imported.map((option) => {
    const reused = take(byLabel, normalizeKey(option.label));
    if (!reused) return option;
    into.set(option.id, reused);
    return { ...option, id: reused };
  });
};

/**
 * Carries the current blueprint's ids onto a re-imported one: blocks by code
 * (falling back to title), questions by label within the matched block (falling
 * back to code), options by label within the matched question.
 *
 * An answer is stored per question and option id, so fresh ids would strand it.
 * Labels lead because the importer re-derives codes from position, so inserting
 * one row renumbers every code after it.
 */
export const reuseExistingIds = (
  existing: FormBlock[],
  imported: FormBlock[]
): FormBlock[] => {
  const blocksByCode = indexBy(existing, (block) => normalizeKey(block.code));
  const blocksByTitle = indexBy(existing, (block) => normalizeKey(block.title));

  const usedBlockIds = new Set<string>();
  const reusedQuestionIds = new Map<string, string>();
  const reusedOptionIds = new Map<string, string>();

  const blocks = imported.map((block) => {
    const match =
      blocksByCode.get(normalizeKey(block.code)) ??
      blocksByTitle.get(normalizeKey(block.title));
    if (!match || usedBlockIds.has(match.id)) return block;
    usedBlockIds.add(match.id);

    const byLabel = indexIdsBy(match.questions ?? [], (q) =>
      normalizeKey(q.label)
    );
    const byCode = indexIdsBy(match.questions ?? [], (q) =>
      normalizeKey(q.code)
    );
    const storedById = new Map((match.questions ?? []).map((q) => [q.id, q]));

    const take = makeIdTaker();

    const questions = (block.questions ?? []).map((question) => {
      const reused =
        take(byLabel, normalizeKey(question.label)) ??
        take(byCode, normalizeKey(question.code));
      if (!reused) return question;
      reusedQuestionIds.set(question.id, reused);
      const options = reuseOptionIds(
        storedById.get(reused)?.options,
        question.options,
        reusedOptionIds
      );
      return options
        ? { ...question, id: reused, options }
        : { ...question, id: reused };
    });

    return { ...block, id: match.id, questions };
  });

  if (reusedQuestionIds.size === 0 && reusedOptionIds.size === 0) return blocks;

  // conditionalOn names a question id and, in `equals`, option ids — both minted
  // by this same import, so both follow the swap. Applies to block- and question-level conditions.
  return blocks.map((block) => ({
    ...block,
    conditionalOn: (() => {
      const condition = block.conditionalOn;
      if (!condition) return condition;
      const questionId =
        reusedQuestionIds.get(condition.questionId) ?? condition.questionId;
      const equals = condition.equals?.map(
        (optionId) => reusedOptionIds.get(optionId) ?? optionId
      );
      return { ...condition, questionId, ...(equals && { equals }) };
    })(),
    questions: (block.questions ?? []).map((question) => {
      const condition = question.conditionalOn;
      if (!condition) return question;
      const questionId =
        reusedQuestionIds.get(condition.questionId) ?? condition.questionId;
      const equals = condition.equals?.map(
        (optionId) => reusedOptionIds.get(optionId) ?? optionId
      );
      const unchanged =
        questionId === condition.questionId &&
        equals?.every((id, index) => id === condition.equals?.[index]) !==
          false;
      return unchanged
        ? question
        : {
            ...question,
            conditionalOn: {
              ...condition,
              questionId,
              ...(equals && { equals }),
            },
          };
    }),
  }));
};

// Makes block codes and question codes each unique across the form (first wins,
// later collisions suffixed "-2", "-3", …), for when an import is merged onto
// existing blocks and the backend's per-import dedup can't see the clash.
export const ensureUniqueCodes = (blocks: FormBlock[]): FormBlock[] => {
  const usedBlockCodes = new Set<string>();
  const usedQuestionCodes = new Set<string>();
  const unique = (code: string, used: Set<string>): string => {
    let candidate = code;
    let suffix = 2;
    while (used.has(candidate.toLowerCase())) {
      candidate = `${code}-${suffix}`;
      suffix += 1;
    }
    used.add(candidate.toLowerCase());
    return candidate;
  };
  return blocks.map((block) => ({
    ...block,
    code: block.code?.trim()
      ? unique(block.code.trim(), usedBlockCodes)
      : block.code,
    questions: (block.questions ?? []).map((question) => ({
      ...question,
      code: question.code?.trim()
        ? unique(question.code.trim(), usedQuestionCodes)
        : question.code,
    })),
  }));
};
